import * as Y from 'yjs';
import { Schema, Node as PMNode } from 'prosemirror-model';
import { MarkdownSerializer } from 'prosemirror-markdown';
import { customMarkdownSerializer } from '../../apps/DocsApp/utils/documentSerializers';

/**
 * Safe YJS text extraction utilities that avoid toString() contamination
 * 
 * This utility provides the correct way to extract text from YJS documents:
 * YJS → ProseMirror → Text/Markdown (never YJS.toString() → XML contamination)
 */
export class SafeYJSExtraction {
  /**
   * Extract content from YJS document with flexible return options
   */
  static extractContent(
    ydoc: Y.Doc, 
    schema: Schema, 
    options: {
      fragmentKey?: string;
      customSerializer?: MarkdownSerializer;
      returnBoth?: boolean;
      filterForAI?: boolean;
    } = {}
  ): { 
    markdown?: string; 
    plainText?: string; 
    pmJSON?: any; 
    pmNode?: PMNode;
    success: boolean;
    error?: string;
  } {
    try {
      const fragmentKey = options.fragmentKey || 'prosemirror';
      const yFrag = ydoc.getXmlFragment(fragmentKey);
      
      if (!yFrag || yFrag.length === 0) {
        // ✅ NORMAL: Empty documents are valid and should be handled gracefully
        // Document is empty - this is normal (verbose logs removed)
        
        return {
          markdown: '',
          plainText: '',
          pmJSON: undefined,
          pmNode: undefined,
          success: true // Empty is success, not failure
        };
      }

      // Step 1: Convert YJS → ProseMirror JSON using official y-prosemirror utilities
      let pmJSON: any;
      
      // Use y-prosemirror conversion if available, otherwise manual conversion
      if (typeof (globalThis as any).yXmlFragmentToProsemirrorJSON === 'function') {
        pmJSON = (globalThis as any).yXmlFragmentToProsemirrorJSON(yFrag, schema);
      } else {
        // Fallback: manual conversion (TODO: import y-prosemirror properly)
        pmJSON = this.manualYJSConversion(yFrag, schema);
      }

      // Step 2: Apply AI filtering if requested
      if (options.filterForAI) {
        pmJSON = this.filterForAI(pmJSON);
      }

      // Step 3: Create ProseMirror node
      const pmNode = PMNode.fromJSON(schema, pmJSON);

      // Step 4: Extract text/markdown as requested
      const result: any = {
        success: true,
        pmJSON: options.returnBoth ? pmJSON : undefined,
        pmNode: options.returnBoth ? pmNode : undefined
      };

      // Plain text extraction (canonical PM method)
      result.plainText = pmNode.textBetween(0, pmNode.content.size, '\n\n');

      // Markdown extraction with custom serializer that understands our schema
      // 🚨 CRITICAL FIX: Use customMarkdownSerializer instead of defaultMarkdownSerializer
      // The default serializer doesn't know about bullet_list, blockquote, tables, etc.
      const serializer = options.customSerializer || customMarkdownSerializer;
      result.markdown = serializer.serialize(pmNode);

      return result;

    } catch (error) {
      console.error('[SafeYJSExtraction] Extraction failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown extraction error'
      };
    }
  }

  /**
   * Filter ProseMirror JSON to remove suggestion artifacts for AI consumption
   * Optimized for memory efficiency with large documents
   */
  static filterForAI(pmJSON: any): any {
    if (!pmJSON) return pmJSON;

    let filteredCount = 0;
    let totalCount = 0;
    const keptNodes: string[] = [];
    const filteredNodes: string[] = [];

    function walk(node: any): any | null {
      // 🚨 DEFENSIVE: Handle null/undefined nodes gracefully (collaborative editing race conditions)
      if (!node || typeof node !== 'object') {
        return null;
      }
      totalCount++;

      // 🚨 CRITICAL: Drop nodes marked for deletion or as old suggestions
      if (node.attrs?.change_status === 'old' || 
          node.attrs?.diff_type === 'agent_delete') {
        filteredCount++;
        filteredNodes.push(node.attrs?.id || 'unknown');
        console.log('[SafeYJSExtraction] Filtered OLD suggestion node:', {
          nodeType: node.type,
          changeStatus: node.attrs?.change_status,
          diffType: node.attrs?.diff_type,
          nodeId: node.attrs?.id,
          reason: 'Old content being replaced by new suggestions'
        });
        return null;
      } else if (node.attrs?.id) {
        keptNodes.push(node.attrs.id);
      }
      
      // ✅ KEEP NEW NODES: Show Claude its own edits so it knows what it already did
      // The new nodes with change_status="new" should be visible to Claude
      // System prompt tells Claude not to re-edit content it already modified

      // 🚨 MEMORY OPTIMIZATION: Only create new objects when necessary
      let hasChanges = false;
      let newContent: any[] | undefined;
      let newMarks: any[] | undefined;

      // Filter children only if they exist
      if (node.content && node.content.length > 0) {
        const filteredContent = node.content.map(walk).filter(Boolean);
        if (filteredContent.length !== node.content.length) {
          newContent = filteredContent;
          hasChanges = true;
        }
      }
      
      // Filter marks only if they exist
      if (node.marks && node.marks.length > 0) {
        const filteredMarks = node.marks.filter((m: any) =>
          m.attrs?.change_status !== 'old' && 
          m.attrs?.diff_type !== 'agent_delete'
        );
        if (filteredMarks.length !== node.marks.length) {
          newMarks = filteredMarks;
          hasChanges = true;
        }
      }

      // Only create new object if changes were made (memory optimization)
      if (hasChanges) {
        return { 
          ...node, 
          ...(newContent !== undefined ? { content: newContent } : {}), 
          ...(newMarks !== undefined ? { marks: newMarks } : {}) 
        };
      }

      // Return original node if no changes needed
      return node;
    }

    const filtered = walk(pmJSON);
    
    console.log('[SafeYJSExtraction] AI filtering complete:', {
      originalNodes: totalCount,
      filteredNodes: totalCount - filteredCount,
      nodesRemoved: filteredCount,
      memoryOptimized: 'Only creates new objects when filtering needed',
      keptNodeIds: keptNodes.slice(0, 10), // First 10 for debugging
      filteredNodeIds: filteredNodes.slice(0, 10) // First 10 for debugging
    });

    return filtered;
  }

  /**
   * Create canonical hash of PM JSON for content stability tracking
   */
  static canonicalHash(pmJSON: any): string {
    if (!pmJSON) return '';

    // Create stable representation for hashing
    const canonical = this.canonicalize(pmJSON);
    
    // Simple hash function (can be replaced with crypto hash if needed)
    let hash = 0;
    const str = JSON.stringify(canonical);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  /**
   * Canonicalize PM JSON for stable hashing
   */
  private static canonicalize(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(item => this.canonicalize(item));

    // Sort object keys for stable representation
    const sorted: any = {};
    const keys = Object.keys(obj).sort();
    
    for (const key of keys) {
      // Strip volatile attributes that shouldn't affect content hash
      if (key === 'revision_id' || key === 'timestamp' || key.startsWith('_')) {
        continue;
      }
      sorted[key] = this.canonicalize(obj[key]);
    }
    
    return sorted;
  }

  /**
   * Count nodes in PM JSON for debugging
   */
  private static countNodes(pmJSON: any): number {
    if (!pmJSON) return 0;
    
    let count = 1; // Count this node
    if (pmJSON.content) {
      count += pmJSON.content.reduce((sum: number, child: any) => 
        sum + this.countNodes(child), 0);
    }
    return count;
  }

  /**
   * Manual YJS conversion fallback (when y-prosemirror utils not available)
   */
  private static manualYJSConversion(yFrag: Y.XmlFragment, schema: Schema): any {
    console.warn('[SafeYJSExtraction] Using manual YJS conversion - implement proper y-prosemirror import');
    
    // Extract content manually from YJS fragment
    const content: any[] = [];
    
    try {
      // 🚨 DEFENSIVE: Ensure yFrag exists and has a valid length
      if (!yFrag || typeof yFrag.length !== 'number') {
        console.error('[SafeYJSExtraction] Invalid YJS fragment in manual conversion');
        return { type: 'doc', content: [] };
      }
      
      for (let i = 0; i < yFrag.length; i++) {
        const element = yFrag.get(i);
        
        // 🚨 DEFENSIVE: Skip null/undefined elements (race condition during collaborative editing)
        if (!element) {
          console.warn(`[SafeYJSExtraction] Skipping null element at index ${i} (length: ${yFrag.length})`);
          continue;
        }
        
        // 🚨 DEFENSIVE: Check element has basic properties before accessing
        if (typeof element !== 'object') {
          console.warn(`[SafeYJSExtraction] Skipping non-object element at index ${i}:`, typeof element);
          continue;
        }
        
        try {
          // Extract element information
          const elementData: any = {
            type: 'paragraph', // Default type
            attrs: {},
            content: [] // Initialize content array
          };
          
          // Get element attributes if available
          if (typeof element.getAttribute === 'function') {
            const id = element.getAttribute('id');
            const type = element.getAttribute('type') || (element as any).nodeName;
            
            // 🚨 CRITICAL FIX: Use getAttributes() (plural) to read YJS attributes correctly
            // The singular getAttribute() method doesn't work for custom attributes like change_status
            const rawAttrs = element.getAttributes ? element.getAttributes() : {};
            
            if (id) elementData.attrs.id = id;
            if (type) elementData.type = type;
            
            Object.keys(rawAttrs).forEach(attrKey => {
              const attrValue = rawAttrs[attrKey];
              if (attrValue !== null && attrValue !== undefined) {
                elementData.attrs[attrKey] = attrValue;
              }
            });

            // Y.js can serialize numeric attributes as strings (e.g. heading level "1").
            // ProseMirror schemas validate attribute types strictly, so coerce known
            // numeric attrs back to numbers to prevent fromJSON() RangeErrors.
            if (elementData.attrs.level !== undefined && typeof elementData.attrs.level === 'string') {
              console.log(`[YjsExtraction] Coercing heading level "${elementData.attrs.level}" → ${Number(elementData.attrs.level)}`);
              elementData.attrs.level = Number(elementData.attrs.level);
            }
            if (elementData.attrs.indent !== undefined && typeof elementData.attrs.indent === 'string') {
              elementData.attrs.indent = Number(elementData.attrs.indent);
            }
          }
          
          // 🛡️ DEFENSIVE: Ensure image nodes always have required src attribute
          const nodeType = (element as any).nodeName || elementData.type;
          if (nodeType === 'image') {
            // Images MUST have a src attribute (no default in schema)
            if (!elementData.attrs.src) {
              console.error('[SafeYJSExtraction] Image node missing src attribute!', {
                nodeType,
                availableAttrs: Object.keys(elementData.attrs),
                rawAttrs: element.getAttributes ? element.getAttributes() : {}
              });
              // Set empty string as fallback to prevent hard failure
              // This allows document to load (with broken image) rather than crashing
              elementData.attrs.src = '';
            }
          }
          
          // 🚀 CRITICAL FIX: Handle lists properly by recursively extracting list_items
          if (nodeType === 'bullet_list' || nodeType === 'ordered_list') {
            console.log(`🚀 [MANUAL_CONVERSION_LIST] Extracting ${nodeType} with ${(element as any).length} children`);
            
            // Extract list_items
            const listItems: any[] = [];
            
            // 🚨 DEFENSIVE: Ensure element has valid length before iterating
            if (typeof (element as any).length === 'number') {
              for (let j = 0; j < (element as any).length; j++) {
                const listItem = (element as any).get(j);
                
                // 🚨 DEFENSIVE: Skip null/undefined list items
                if (!listItem || typeof listItem !== 'object') {
                  console.warn(`[SafeYJSExtraction] Skipping null/invalid list item at index ${j}`);
                  continue;
                }
                
                if ((listItem as any).nodeName === 'list_item') {
                  // Extract content from list_item
                  const listItemContent: any[] = [];
                  
                  // 🚨 DEFENSIVE: Ensure listItem has valid length
                  if (typeof (listItem as any).length === 'number') {
                    // List items contain paragraphs or other block content
                    for (let k = 0; k < (listItem as any).length; k++) {
                      const itemChild = (listItem as any).get(k);
                      
                      // 🚨 DEFENSIVE: Skip null/undefined children
                      if (!itemChild || typeof itemChild !== 'object') {
                        continue;
                      }
                      
                      const itemChildType = (itemChild as any).nodeName || 'paragraph';
                      const itemChildData: any = {
                        type: itemChildType,
                        content: []
                      };
                      
                      // Extract text from the paragraph inside the list_item
                      if (typeof (itemChild as any).length === 'number') {
                        for (let m = 0; m < (itemChild as any).length; m++) {
                          const textNode = (itemChild as any).get(m);
                          
                          // 🚨 DEFENSIVE: Skip null/undefined text nodes
                          if (!textNode || typeof textNode.toDelta !== 'function') {
                            continue;
                          }
                          
                          try {
                            const delta = textNode.toDelta();
                            const text = delta.map((op: any) => op.insert || '').join('');
                            if (text) {
                              itemChildData.content.push({ type: 'text', text: text });
                            }
                          } catch (e) {
                            console.warn('[SafeYJSExtraction] Text extraction failed in list_item:', e);
                          }
                        }
                      }
                      
                      listItemContent.push(itemChildData);
                    }
                  }
                  
                  listItems.push({
                    type: 'list_item',
                    content: listItemContent
                  });
                }
              }
            }
            
            elementData.content = listItems;
            console.log(`✅ [MANUAL_CONVERSION_LIST] Extracted ${listItems.length} list items`);
            
          } else {
            // Extract text content safely for non-list elements
            if (element && typeof (element as any).length === 'number') {
              const textContent: any[] = [];
              for (let j = 0; j < (element as any).length; j++) {
                const child = (element as any).get(j);
                
                // 🚨 DEFENSIVE: Skip null/undefined children
                if (!child || typeof child !== 'object') {
                  continue;
                }
                
                // 🚨 DEFENSIVE: Check toDelta exists before calling
                if (typeof child.toDelta === 'function') {
                  try {
                    const delta = child.toDelta();
                    // 🚨 DEFENSIVE: Ensure delta is an array
                    if (Array.isArray(delta)) {
                      const text = delta.map((op: any) => op.insert || '').join('');
                      if (text) {
                        textContent.push({
                          type: 'text',
                          text: text
                        });
                      }
                    }
                  } catch (deltaError) {
                    console.warn('[SafeYJSExtraction] Delta extraction failed in manual conversion:', deltaError);
                  }
                }
              }
              
              if (textContent.length > 0) {
                elementData.content = textContent;
              } else {
                // Handle elements without text content (like empty paragraphs)
                elementData.content = [];
              }
            } else {
              // Handle elements that aren't text containers
              elementData.content = [];
            }
          }
          
          // Only add non-empty elements or elements with IDs
          if (elementData.content.length > 0 || elementData.attrs.id) {
            content.push(elementData);
          }
        } catch (elementError) {
          // 🚨 DEFENSIVE: Catch errors processing individual elements to prevent entire conversion from failing
          // This is critical for collaborative editing where elements might be modified during extraction
          console.error(`[SafeYJSExtraction] Failed to process element at index ${i} (non-fatal):`, {
            error: elementError instanceof Error ? elementError.message : String(elementError),
            elementType: typeof element,
            fragmentLength: yFrag.length
          });
          // Continue with next element instead of failing entire conversion
        }
      }
      
    } catch (error) {
      console.error('[SafeYJSExtraction] Manual conversion failed:', error);
    }
    
    return {
      type: 'doc',
      content: content
    };
  }

  /**
   * Simple boundary sanitizer (minimal safety net)
   */
  static boundaryCleanup(text: string): string {
    if (!text) return text;
    
    // Ultra-simple cleanup: remove NULs and malformed angle brackets
    return text
      .replace(/\0/g, '') // Remove NULL characters
      .replace(/<(?![a-zA-Z/])/g, '&lt;') // Escape malformed < characters
      .replace(/(?<![a-zA-Z])>/g, '&gt;') // Escape malformed > characters
      .trim();
  }
}

/**
 * Convenience exports for backward compatibility
 */
export const extractCleanText = (ydoc: Y.Doc, schema: Schema) => 
  SafeYJSExtraction.extractContent(ydoc, schema).plainText || '';

export const extractCleanMarkdown = (ydoc: Y.Doc, schema: Schema) => 
  SafeYJSExtraction.extractContent(ydoc, schema).markdown || '';

export const extractForAI = (ydoc: Y.Doc, schema: Schema) => 
  SafeYJSExtraction.extractContent(ydoc, schema, { filterForAI: true });
